import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowLeft, Clock, Users, Star, ChefHat, Heart, Flame, Timer,
  Edit, Trash2, AlertTriangle, ShoppingCart, Plus, Check, X,
  Package, Link, UtensilsCrossed, CheckCircle2,
  ThumbsUp, MessageSquare, ChevronDown, ChevronUp,
  Play, ChevronLeft, ChevronRight, StopCircle, Share2, Download, Copy, QrCode,
  Info, Leaf, Tag, BarChart2,
} from 'lucide-react';
import { Card, Button } from '../UI';
import { auth, db } from '../../services/firebase';
import { getRecipeImageURL, softDeleteRecipe } from '../../hooks/useRecipes';
import {
  collection, query, where, getDocs, getDoc,
  updateDoc, doc, orderBy, addDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { AuthContext } from '../../contexts/AuthContext';
import { useGuestList } from '../../hooks/useGuestList';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DIETARY_TAG_COLORS = {
  vegan: '#22c55e',
  vegetarian: '#86efac',
  pescatarian: '#3b82f6',
  keto: '#f97316',
  paleo: '#a16207',
  glutenfree: '#eab308',
  dairyfree: '#60a5fa',
  nutfree: '#c2410c',
  lowcarb: '#a855f7',
  halal: '#15803d',
  kosher: '#1d4ed8',
  highprotein: '#06b6d4',
};

const NAV_TABS = [
  { id: 'overview',     label: 'Overview'     },
  { id: 'ingredients',  label: 'Ingredients'  },
  { id: 'instructions', label: 'Instructions' },
  { id: 'nutrition',    label: 'Nutrition'    },
  { id: 'reviews',      label: 'Reviews'      },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const scaleQty = (qty, servings, baseServings) => {
  const num = parseFloat(qty);
  if (isNaN(num) || !baseServings) return qty;
  const scaled = num * (servings / baseServings);
  return scaled % 1 === 0 ? scaled.toString() : scaled.toFixed(1);
};

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : ts.toDate?.() ?? new Date(ts);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getStepData = (step, index) => ({
  instruction: typeof step === 'string' ? step : (step.instruction || step.text || ''),
  order:       typeof step === 'string' ? index + 1 : (step.order || step.stepNumber || index + 1),
  timerMins:   typeof step === 'string' ? null : step.timerMinutes,
  imageURL:    typeof step === 'string' ? null : (step.imageURL || step.imageUrl || null),
  id:          step.id ?? index,
});

// ---------------------------------------------------------------------------
// InfoCard — matches iOS infoCard container style
// ---------------------------------------------------------------------------

const InfoCard = ({ title, icon: Icon, children }) => (
  <div
    className="rounded-large p-lg bg-surface"
    style={{ border: '1px solid rgba(var(--color-primary-rgb),0.08)', boxShadow: '0 2px 4px rgba(var(--color-primary-rgb),0.04)' }}
  >
    <div className="flex items-center gap-sm mb-md">
      <Icon size={14} className="text-primary flex-shrink-0" />
      <p className="text-callout font-bold text-text-primary">{title}</p>
    </div>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// NutritionTile — matches iOS 2-column tile grid
// ---------------------------------------------------------------------------

const NutritionTile = ({ label, value, unit }) => (
  <div
    className="rounded-large bg-surface flex flex-col items-center justify-center py-md px-sm"
    style={{ border: '1px solid rgba(var(--color-primary-rgb),0.07)' }}
  >
    <div className="flex items-baseline gap-xs">
      <span className="text-title-2 font-bold text-primary">{value}</span>
      {unit && <span className="text-caption text-text-tertiary">{unit}</span>}
    </div>
    <span className="text-caption text-text-secondary mt-xs">{label}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Add to List Modal
// ---------------------------------------------------------------------------

const AddToListModal = ({ recipe, onClose }) => {
  const { user } = useContext(AuthContext);
  const { addItem: addGuestItem } = useGuestList();

  const [lists, setLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedIngredientIds, setSelectedIngredientIds] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState(false);
  const [addedCount, setAddedCount] = useState(0);

  const ingredients = recipe.ingredients || recipe.ingredientsList || [];

  useEffect(() => {
    setSelectedIngredientIds(new Set(ingredients.map((_, i) => i)));
    if (!user) { setLoadingLists(false); return; }
    const fetchLists = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'groceryLists'), where('ownerId', '==', user.uid), where('isArchived', '==', false), orderBy('updatedAt', 'desc'))
        );
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setLists(data);
        if (data.length > 0) setSelectedListId(data[0].id);
      } catch (err) { console.error(err); }
      finally { setLoadingLists(false); }
    };
    fetchLists();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleIngredient = (i) => setSelectedIngredientIds(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  const toggleAll = () => setSelectedIngredientIds(
    selectedIngredientIds.size === ingredients.length
      ? new Set()
      : new Set(ingredients.map((_, i) => i))
  );

  const buildItem = (ingredient, index) => {
    const isStr  = typeof ingredient === 'string';
    const name   = isStr ? ingredient : ingredient.name;
    const qty    = isStr ? null : (ingredient.quantity || ingredient.amount);
    const unit   = isStr ? null : ingredient.unit;
    return {
      id: `${Date.now()}_${index}_${Math.random()}`,
      name: (!isStr && ingredient.linkedProductName) || name,
      quantity: qty ? parseFloat(qty) || 1 : 1,
      unit: unit || '',
      category: 'recipe',
      isChecked: false,
      addedAt: new Date(),
      addedFromRecipeId: recipe.firestoreId || recipe.id,
      addedFromRecipeTitle: recipe.title,
      ...(!isStr && ingredient.linkedProductId ? {
        productId: ingredient.linkedProductId,
        productImageURL: ingredient.linkedProductImageURL,
      } : {}),
    };
  };

  const handleAdd = async () => {
    if (selectedIngredientIds.size === 0) return;
    setAdding(true);
    const items = ingredients
      .filter((_, i) => selectedIngredientIds.has(i))
      .map((ing, i) => buildItem(ing, i));
    try {
      if (!user) {
        items.forEach(item => addGuestItem(item));
      } else {
        const targetList = lists.find(l => l.id === selectedListId);
        await updateDoc(doc(db, 'groceryLists', selectedListId), {
          items: [...(targetList?.items || []), ...items],
          updatedAt: new Date(),
        });
      }
      setAddedCount(items.length);
      setDone(true);
    } catch (err) { console.error(err); }
    finally { setAdding(false); }
  };

  const selectedList = lists.find(l => l.id === selectedListId);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-lg">
      <div className="bg-background rounded-large border border-text-tertiary/20 w-full max-w-lg shadow-xl max-h-[90vh] flex flex-col">
        <div className="p-lg border-b border-text-tertiary/10 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-title-3 font-semibold text-primary">Add to Shopping List</h3>
            <p className="text-caption text-text-secondary mt-xs">{recipe.title}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
            <X size={18} className="text-text-tertiary" />
          </button>
        </div>

        {done ? (
          <div className="p-xl text-center flex-1 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-lg">
              <Check size={32} className="text-success" />
            </div>
            <h4 className="text-title-3 font-semibold text-text-primary mb-sm">
              {addedCount} ingredient{addedCount !== 1 ? 's' : ''} added
            </h4>
            <p className="text-body text-text-secondary mb-lg">
              {user ? `Added to "${selectedList?.name || 'your list'}"` : 'Added to your guest list'}
            </p>
            <Button variant="primary" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">
              {user && (
                <div className="p-lg border-b border-text-tertiary/10">
                  <p className="text-caption text-text-secondary mb-sm">Add to list</p>
                  {loadingLists ? (
                    <div className="h-10 bg-surface rounded-medium animate-pulse" />
                  ) : lists.length === 0 ? (
                    <p className="text-caption text-text-tertiary py-sm">
                      No active lists. Create one in the Shopping Lists tab.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-sm">
                      {lists.map(list => (
                        <button
                          key={list.id}
                          onClick={() => setSelectedListId(list.id)}
                          className={`px-md py-sm rounded-full text-caption font-semibold transition-all border ${
                            selectedListId === list.id
                              ? 'bg-primary text-white border-primary'
                              : 'bg-surface border-text-tertiary/20 text-text-secondary hover:border-primary hover:text-primary'
                          }`}
                        >
                          {list.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!user && (
                <div className="p-lg border-b border-text-tertiary/10">
                  <div className="flex items-center gap-sm p-md rounded-medium bg-accent/5 border border-accent/20">
                    <ShoppingCart size={16} className="text-accent flex-shrink-0" />
                    <p className="text-caption text-text-secondary">
                      Adding to guest list (stored locally). Sign in to sync across devices.
                    </p>
                  </div>
                </div>
              )}
              <div className="p-lg">
                <div className="flex items-center justify-between mb-md">
                  <p className="text-callout font-semibold text-text-primary">
                    Select ingredients ({selectedIngredientIds.size}/{ingredients.length})
                  </p>
                  <button onClick={toggleAll} className="text-caption text-primary hover:text-primary/80 font-semibold">
                    {selectedIngredientIds.size === ingredients.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <div className="space-y-sm">
                  {ingredients.map((ingredient, index) => {
                    const isStr    = typeof ingredient === 'string';
                    const name     = isStr ? ingredient : ingredient.name;
                    const qty      = isStr ? null : (ingredient.quantity || ingredient.amount);
                    const unit     = isStr ? null : ingredient.unit;
                    const isLinked = !isStr && !!ingredient.linkedProductId;
                    const isSel    = selectedIngredientIds.has(index);
                    return (
                      <button
                        key={ingredient.id || index}
                        onClick={() => toggleIngredient(index)}
                        className={`w-full flex items-center gap-md p-md rounded-medium border transition-all text-left ${
                          isSel ? 'border-primary/30 bg-primary/5' : 'border-text-tertiary/10 hover:border-text-tertiary/20'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isSel ? 'border-primary bg-primary' : 'border-text-tertiary/30'
                        }`}>
                          {isSel && <Check size={12} className="text-white" />}
                        </div>
                        {isLinked && ingredient.linkedProductImageURL ? (
                          <div className="w-9 h-9 rounded-medium bg-white border border-text-tertiary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            <img src={ingredient.linkedProductImageURL} alt={name} className="w-full h-full object-contain p-xs" />
                          </div>
                        ) : (
                          <div className="w-9 h-9 rounded-medium bg-surface border border-text-tertiary/10 flex items-center justify-center flex-shrink-0">
                            <Package size={14} className="text-text-tertiary" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-callout text-text-primary">
                            {qty ? `${qty} ` : ''}{unit ? `${unit} ` : ''}
                            <span className="font-medium">{name}</span>
                          </p>
                          {isLinked && (
                            <p className="text-caption text-primary flex items-center gap-xs mt-xs">
                              <Link size={10} />{ingredient.linkedProductName || 'Linked product'}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="p-lg border-t border-text-tertiary/10 flex gap-md flex-shrink-0">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button
                variant="primary"
                onClick={handleAdd}
                disabled={
                  adding ||
                  selectedIngredientIds.size === 0 ||
                  (user && !selectedListId) ||
                  (user && lists.length === 0)
                }
                className="flex-1"
              >
                {adding ? 'Adding...' : `Add ${selectedIngredientIds.size} ingredient${selectedIngredientIds.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Add Review Modal
// ---------------------------------------------------------------------------

const AddReviewModal = ({ recipeId, onClose, onSubmitted }) => {
  const { user } = useContext(AuthContext);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0 || !user) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'recipeReviews'), {
        recipeId,
        userId: user.uid,
        authorName: user.displayName || user.email?.split('@')[0] || 'User',
        rating,
        comment: comment.trim(),
        likes: 0,
        createdAt: serverTimestamp(),
      });
      const recipeRef  = doc(db, 'recipes', recipeId);
      const recipeSnap = await getDoc(recipeRef);
      if (recipeSnap.exists()) {
        const data       = recipeSnap.data();
        const prevCount  = data.reviewCount || 0;
        const prevRating = data.rating || 0;
        const newCount   = prevCount + 1;
        const newRating  = ((prevRating * prevCount) + rating) / newCount;
        await updateDoc(recipeRef, { rating: newRating, reviewCount: newCount });
      }
      onSubmitted();
    } catch (err) {
      console.error('Error submitting review:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-lg">
      <div className="bg-background rounded-large border border-text-tertiary/20 w-full max-w-md shadow-xl">
        <div className="p-lg border-b border-text-tertiary/10 flex items-center justify-between">
          <h3 className="text-title-3 font-semibold text-primary">Write a Review</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
            <X size={18} className="text-text-tertiary" />
          </button>
        </div>
        <div className="p-lg space-y-lg">
          <div>
            <p className="text-callout font-semibold text-text-primary mb-md">Your Rating *</p>
            <div className="flex gap-sm">
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  key={s}
                  onMouseEnter={() => setHoverRating(s)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(s)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={32}
                    className={s <= (hoverRating || rating) ? 'text-accent fill-accent' : 'text-text-tertiary'}
                  />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-callout font-semibold text-text-primary mb-sm block">Comment</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Share your experience with this recipe..."
              rows={4}
              className="w-full px-lg py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none resize-none text-body text-text-primary"
            />
          </div>
          <div className="flex gap-md">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={rating === 0 || submitting} className="flex-1">
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Cooking Player
// ---------------------------------------------------------------------------

const StartRecipeButton = ({ onClick, variant = 'hero' }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-sm font-semibold transition-all rounded-large px-lg py-md ${
      variant === 'hero'
        ? 'bg-white text-primary hover:bg-white/90 text-callout'
        : 'bg-primary/10 text-primary hover:bg-primary/15 border border-primary/20 text-caption'
    }`}
  >
    <Play size={variant === 'hero' ? 16 : 14} className="fill-current" />
    Start Cooking
  </button>
);

const CookingPlayer = ({ steps, currentIndex, onPrev, onNext, onExit, checkedSteps, onToggleStep }) => {
  const step   = steps[currentIndex];
  const s      = getStepData(step, currentIndex);
  const isDone = checkedSteps.has(s.id);

  return (
    <div
      className="rounded-large overflow-hidden mb-xl"
      style={{ border: '1px solid rgba(var(--color-primary-rgb),0.15)', background: 'rgba(var(--color-primary-rgb),0.03)' }}
    >
      {/* Header */}
      <div className="px-lg py-md flex items-center justify-between border-b border-text-tertiary/10">
        <div className="flex items-center gap-sm">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-caption font-semibold text-text-secondary uppercase tracking-wide">Cooking Mode</span>
        </div>
        <span className="text-caption text-text-tertiary">Step {currentIndex + 1} of {steps.length}</span>
      </div>

      {/* Step image */}
      {s.imageURL && (
        <div className="w-full aspect-video overflow-hidden bg-surface">
          <img src={s.imageURL} alt={`Step ${s.order}`} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Step content */}
      <div className="p-lg">
        <div className="flex gap-md mb-md">
          <button onClick={() => onToggleStep(s.id)} className="flex-shrink-0 mt-xs transition-all">
            {isDone ? (
              <CheckCircle2 size={28} className="text-primary fill-primary/10" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                <span className="text-caption font-bold text-white">{s.order}</span>
              </div>
            )}
          </button>
          <p className={`text-body text-text-primary leading-relaxed flex-1 ${isDone ? 'line-through text-text-tertiary' : ''}`}>
            {s.instruction}
          </p>
        </div>
        {s.timerMins && (
          <div className="flex items-center gap-xs px-md py-xs bg-primary/5 rounded-small w-fit mb-md">
            <Timer size={14} className="text-primary" />
            <span className="text-caption font-semibold text-primary">{s.timerMins} min</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="px-lg pb-md flex items-center justify-between">
        <div className="flex gap-sm">
          {currentIndex > 0 && (
            <button
              onClick={onPrev}
              className="flex items-center gap-xs px-md py-sm rounded-large text-caption font-semibold bg-surface border border-text-tertiary/20 text-text-secondary hover:border-primary hover:text-primary transition-all"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
          )}
          {currentIndex < steps.length - 1 && (
            <button
              onClick={onNext}
              className="flex items-center gap-xs px-md py-sm rounded-large text-caption font-semibold bg-primary text-white hover:bg-primary/85 transition-all"
            >
              Next
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Exit */}
      <div className="px-lg pb-lg">
        <button
          onClick={onExit}
          className="flex items-center gap-xs text-caption text-text-tertiary hover:text-error transition-colors"
        >
          <StopCircle size={13} />
          Exit cooking mode
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sticky Nav
// ---------------------------------------------------------------------------

const StickyNav = ({ activeSection, onNavigate, hasNutrition, reviewCount }) => {
  const tabs = NAV_TABS.filter(t => {
    if (t.id === 'nutrition' && !hasNutrition) return false;
    return true;
  });

  return (
    <div
      className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-text-tertiary/10 -mx-lg px-lg mb-xl"
    >
      <div className="flex gap-0 overflow-x-auto scrollbar-hide">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => onNavigate(tab.id)}
            className={`px-lg py-md text-caption font-semibold whitespace-nowrap border-b-2 transition-all ${
              activeSection === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
            {tab.id === 'reviews' && reviewCount > 0 && (
              <span className="ml-xs text-text-tertiary font-normal">({reviewCount})</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Review Card
// ---------------------------------------------------------------------------

const ReviewCard = ({ review, hasVoted, onVote, isAuthenticated }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.comment?.length > 200;

  return (
    <div
      className="p-lg rounded-large bg-surface"
      style={{ border: '1px solid rgba(var(--color-text-tertiary),0.08)' }}
    >
      <div className="flex items-start justify-between gap-md mb-md">
        <div className="flex items-center gap-md">
          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <span className="text-caption font-bold text-white">
              {(review.authorName || 'U').charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-callout font-semibold text-text-primary">{review.authorName || 'User'}</p>
            <p className="text-xs text-text-tertiary">{formatDate(review.createdAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-xs flex-shrink-0">
          {[1, 2, 3, 4, 5].map(s => (
            <Star
              key={s}
              size={14}
              className={s <= Math.round(review.rating) ? 'text-accent fill-accent' : 'text-text-tertiary'}
            />
          ))}
        </div>
      </div>

      {review.comment && (
        <div className="mb-md">
          <p className="text-body text-text-secondary leading-relaxed">
            {isLong && !expanded ? `${review.comment.slice(0, 200)}...` : review.comment}
          </p>
          {isLong && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="mt-xs text-caption text-primary hover:text-primary/80 flex items-center gap-xs"
            >
              {expanded
                ? <><ChevronUp size={13} />Show less</>
                : <><ChevronDown size={13} />Read more</>
              }
            </button>
          )}
        </div>
      )}

      {isAuthenticated && (
        <button
          onClick={onVote}
          className={`flex items-center gap-xs px-md py-xs rounded-full text-caption font-semibold transition-all ${
            hasVoted ? 'bg-primary text-white' : 'bg-primary/8 text-primary hover:bg-primary/15'
          }`}
        >
          <ThumbsUp size={13} />
          {review.likes || 0} Helpful
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Nutrition Row (used in reviews section fallback)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// RecipeShareModal
// ---------------------------------------------------------------------------

const RecipeShareModal = ({ recipe, onClose }) => {
  const recipeId  = recipe.firestoreId || recipe.id;
  const recipeUrl = `https://nutrius.co/recipes/${recipeId}`;
  const qrUrl     = `https://chart.googleapis.com/chart?cht=qr&chs=300x300&chl=${encodeURIComponent(recipeUrl)}&choe=UTF-8`;

  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(recipeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = recipeUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const printQR = () => {
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>QR Code — ${recipe.title}</title>
          <style>
            body { font-family: system-ui, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; background: #fff; }
            h2 { color: #2D5F4D; font-size: 20px; margin-bottom: 8px; text-align: center; }
            p { color: #666; font-size: 13px; margin: 4px 0; text-align: center; }
            img { margin: 20px 0; }
            .url { font-size: 11px; color: #999; word-break: break-all; max-width: 300px; }
          </style>
        </head>
        <body>
          <h2>${recipe.title}</h2>
          <p>Scan to view recipe and ingredients</p>
          <img src="${qrUrl}" width="260" height="260" />
          <p class="url">${recipeUrl}</p>
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `);
    win.document.close();
  };

  const downloadQR = () => {
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = `${recipe.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-qr.png`;
    a.target = '_blank';
    a.click();
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-lg"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-large p-xl w-full max-w-sm space-y-lg"
        style={{ border: '1px solid rgba(var(--color-text-tertiary),0.15)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-title-3 font-bold text-primary">Share Recipe</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
            <X size={18} className="text-text-tertiary" />
          </button>
        </div>

        <div className="flex justify-center">
          <img src={qrUrl} alt="QR Code" className="w-48 h-48 rounded-large" />
        </div>

        <div
          className="flex items-center gap-sm p-md rounded-medium"
          style={{ background: 'rgba(var(--color-primary-rgb),0.05)', border: '1px solid rgba(var(--color-primary-rgb),0.1)' }}
        >
          <p className="text-caption text-text-secondary flex-1 truncate">{recipeUrl}</p>
          <button
            onClick={copyLink}
            className="flex items-center gap-xs px-sm py-xs rounded-medium text-caption font-semibold transition-all flex-shrink-0"
            style={{ background: copied ? 'var(--color-primary)' : 'rgba(var(--color-primary-rgb),0.08)', color: copied ? '#fff' : 'var(--color-primary)' }}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        <div className="flex gap-sm">
          <button
            onClick={downloadQR}
            className="flex-1 flex items-center justify-center gap-sm py-md rounded-large text-callout font-semibold text-primary transition-all hover:opacity-80"
            style={{ background: 'rgba(var(--color-primary-rgb),0.08)', border: '1px solid rgba(var(--color-primary-rgb),0.15)' }}
          >
            <Download size={16} />
            Download
          </button>
          <button
            onClick={printQR}
            className="flex-1 flex items-center justify-center gap-sm py-md rounded-large text-callout font-semibold text-white transition-all hover:opacity-85"
            style={{ background: 'var(--color-primary)' }}
          >
            <Share2 size={16} />
            Print
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const RecipeDetailView = ({ recipe, onBack, onEdit, isFavorite, onToggleFavorite, isAuthenticated }) => {
  const { user } = useContext(AuthContext);

  // Serving state — integer stepper, matches iOS
  const baseServings                          = recipe.servings || 1;
  const [currentServings, setCurrentServings] = useState(baseServings);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShare,         setShowShare]         = useState(false);
  const [deleting, setDeleting]                   = useState(false);
  const [showAddToList, setShowAddToList]         = useState(false);
  const [showAddReview, setShowAddReview]         = useState(false);
  const [activeSection, setActiveSection]         = useState('overview');
  const [stickyVisible, setStickyVisible]         = useState(false);

  // Cooking mode
  const [cookingActive, setCookingActive]       = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [checkedSteps, setCheckedSteps]         = useState(new Set());

  // Ingredient check state — matches iOS ingredient tab
  const [checkedIngredients, setCheckedIngredients] = useState(new Set());

  // Reviews
  const [reviews, setReviews]                   = useState([]);
  const [reviewsLoading, setReviewsLoading]     = useState(true);
  const [hasUserReviewed, setHasUserReviewed]   = useState(false);
  const [userVotedReviews, setUserVotedReviews] = useState(new Set());

  // Refs
  const heroRef         = useRef(null);
  const cookingRef      = useRef(null);
  const overviewRef     = useRef(null);
  const ingredientsRef  = useRef(null);
  const instructionsRef = useRef(null);
  const nutritionRef    = useRef(null);
  const reviewsRef      = useRef(null);

  const sectionRefs = {
    overview:     overviewRef,
    ingredients:  ingredientsRef,
    instructions: instructionsRef,
    nutrition:    nutritionRef,
    reviews:      reviewsRef,
  };

  // Derived
  const imageURL    = getRecipeImageURL(recipe);
  const isOwner     = auth.currentUser?.uid === (recipe.authorId || recipe.createdBy);
  const steps       = recipe.stepsList?.length > 0    ? recipe.stepsList
                    : recipe.steps?.length > 0         ? recipe.steps
                    : [];
  const ingredients = recipe.ingredientsList?.length > 0 ? recipe.ingredientsList
                    : recipe.ingredients?.length > 0       ? recipe.ingredients
                    : [];
  const hasNutrition = !!(recipe.nutrition && Object.values(recipe.nutrition).some(v => v != null));
  const totalTime    = (recipe.prepTimeMinutes || recipe.prepTime || 0)
                     + (recipe.cookTimeMinutes || recipe.cookTime || 0)
                     || recipe.totalTimeMinutes || 0;
  const stepsComplete = checkedSteps.size;
  const stepsTotal    = steps.length;
  const allStepsDone  = stepsTotal > 0 && stepsComplete === stepsTotal;

  // ---------------------------------------------------------------------------
  // Scroll spy
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const onScroll = () => {
      const heroBottom = heroRef.current?.getBoundingClientRect().bottom ?? 0;
      setStickyVisible(heroBottom < 0);

      const offsets = Object.entries(sectionRefs)
        .map(([id, ref]) => ({ id, top: ref.current?.getBoundingClientRect().top ?? Infinity }))
        .filter(({ top }) => top <= 120);

      if (offsets.length > 0) setActiveSection(offsets[offsets.length - 1].id);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Section navigation
  // ---------------------------------------------------------------------------
  const navigateTo = (sectionId) => {
    const ref = sectionRefs[sectionId];
    if (!ref?.current) return;
    const y = ref.current.getBoundingClientRect().top + window.scrollY - 64;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setActiveSection(sectionId);
  };

  // ---------------------------------------------------------------------------
  // Cooking mode
  // ---------------------------------------------------------------------------
  const startCooking = () => {
    setCurrentStepIndex(0);
    setCookingActive(true);
    setTimeout(() => {
      cookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const exitCooking = () => {
    setCookingActive(false);
    setCurrentStepIndex(0);
  };

  const nextStep = () => {
    const stepData = getStepData(steps[currentStepIndex], currentStepIndex);
    setCheckedSteps(prev => new Set([...prev, stepData.id]));
    setCurrentStepIndex(i => Math.min(i + 1, steps.length - 1));
    setTimeout(() => cookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const prevStep = () => {
    setCurrentStepIndex(i => Math.max(i - 1, 0));
    setTimeout(() => cookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const toggleStep = (id) => setCheckedSteps(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleIngredient = (id) => setCheckedIngredients(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // ---------------------------------------------------------------------------
  // Reviews
  // ---------------------------------------------------------------------------
  const loadReviews = useCallback(async () => {
    const recipeId = recipe.firestoreId || recipe.id;
    if (!recipeId) return;
    try {
      setReviewsLoading(true);
      const snap = await getDocs(
        query(collection(db, 'recipeReviews'), where('recipeId', '==', recipeId), orderBy('createdAt', 'desc'))
      );
      setReviews(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      if (user) {
        const userSnap = await getDocs(
          query(collection(db, 'recipeReviews'), where('recipeId', '==', recipeId), where('userId', '==', user.uid))
        );
        setHasUserReviewed(!userSnap.empty);
        const votesSnap = await getDocs(query(collection(db, 'reviewVotes'), where('userId', '==', user.uid)));
        setUserVotedReviews(new Set(votesSnap.docs.map(d => d.data().reviewId)));
      }
    } catch (err) {
      console.error('Error loading reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  }, [recipe, user]);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  const handleVote = async (reviewId) => {
    if (!user) return;
    const hasVoted  = userVotedReviews.has(reviewId);
    const reviewRef = doc(db, 'recipeReviews', reviewId);
    try {
      if (hasVoted) {
        await deleteDoc(doc(db, 'reviewVotes', `${user.uid}_${reviewId}`));
        const snap = await getDoc(reviewRef);
        if (snap.exists()) await updateDoc(reviewRef, { likes: Math.max(0, (snap.data().likes || 0) - 1) });
        setUserVotedReviews(prev => { const n = new Set(prev); n.delete(reviewId); return n; });
      } else {
        await addDoc(collection(db, 'reviewVotes'), { userId: user.uid, reviewId, createdAt: serverTimestamp() });
        const snap = await getDoc(reviewRef);
        if (snap.exists()) await updateDoc(reviewRef, { likes: (snap.data().likes || 0) + 1 });
        setUserVotedReviews(prev => new Set([...prev, reviewId]));
      }
      await loadReviews();
    } catch (err) { console.error(err); }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const handleDelete = async () => {
    try {
      setDeleting(true);
      await softDeleteRecipe(recipe.firestoreId || recipe.id);
      onBack();
    } catch (e) {
      console.error(e);
      setDeleting(false);
    }
  };

  const ratingBreakdown = (star) => {
    const count = reviews.filter(r => Math.round(r.rating) === star).length;
    return { count, pct: reviews.length > 0 ? (count / reviews.length) * 100 : 0 };
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="max-w-4xl mx-auto">

      {/* Back + owner actions */}
      <div className="flex items-center justify-between mb-lg">
        <button onClick={onBack} className="flex items-center gap-sm text-text-secondary hover:text-primary transition-all">
          <ArrowLeft size={20} />
          <span className="text-callout font-semibold">Back to Recipes</span>
        </button>
        <div className="flex items-center gap-sm">
          {isAuthenticated && (
            <button
              onClick={() => onToggleFavorite && onToggleFavorite(recipe.firestoreId || recipe.id)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                isFavorite ? 'bg-error text-white' : 'bg-surface text-text-secondary hover:text-error'
              }`}
            >
              <Heart size={18} className={isFavorite ? 'fill-current' : ''} />
            </button>
          )}
          <button
            onClick={() => setShowShare(true)}
            className="w-9 h-9 rounded-full bg-surface flex items-center justify-center text-text-secondary hover:text-primary transition-all"
            title="Share recipe"
          >
            <QrCode size={18} />
          </button>
          {isOwner && onEdit && (
            <button onClick={() => onEdit(recipe)} className="w-9 h-9 rounded-full bg-surface flex items-center justify-center text-text-secondary hover:text-primary transition-all">
              <Edit size={18} />
            </button>
          )}
          {isOwner && (
            <button onClick={() => setShowDeleteConfirm(true)} className="w-9 h-9 rounded-full bg-surface flex items-center justify-center text-text-secondary hover:text-error transition-all">
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {/* ── HERO ── */}
      <div ref={heroRef} className="h-72 md:h-[420px] rounded-large overflow-hidden relative mb-lg">
        {imageURL ? (
          <img src={imageURL} alt={recipe.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-accent/10 to-primary/5 flex items-center justify-center">
            <ChefHat size={80} className="text-text-tertiary opacity-30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-lg">
          {recipe.cuisine && (
            <span className="inline-block px-md py-xs rounded-full text-caption font-semibold text-white/90 mb-sm"
              style={{ background: 'rgba(0,0,0,0.3)' }}>
              {recipe.cuisine}
            </span>
          )}
          <h1 className="text-display font-rounded text-white mt-xs leading-tight mb-xs">
            {recipe.title || recipe.name}
          </h1>
          {recipe.authorName && (
            <p className="text-caption text-white/70 mb-md">by {recipe.authorName}</p>
          )}
          {steps.length > 0 && !cookingActive && (
            <StartRecipeButton onClick={startCooking} variant="hero" />
          )}
          {cookingActive && (
            <div
              className="inline-flex items-center gap-sm px-md py-xs rounded-large text-white text-caption font-semibold"
              style={{ background: 'rgba(var(--color-success),0.8)' }}
            >
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              Cooking — Step {currentStepIndex + 1} of {steps.length}
            </div>
          )}
        </div>
      </div>

      {/* ── COOKING PLAYER ── */}
      {cookingActive && steps.length > 0 && (
        <div ref={cookingRef}>
          <CookingPlayer
            steps={steps}
            currentIndex={currentStepIndex}
            onPrev={prevStep}
            onNext={nextStep}
            onExit={exitCooking}
            checkedSteps={checkedSteps}
            onToggleStep={toggleStep}
          />
        </div>
      )}

      {/* ── STICKY NAV ── */}
      {stickyVisible && (
        <StickyNav
          activeSection={activeSection}
          onNavigate={navigateTo}
          hasNutrition={hasNutrition}
          reviewCount={reviews.length}
        />
      )}

      {/* ── OVERVIEW ── */}
      <div ref={overviewRef} id="rdv-overview" className="space-y-lg mb-xl">

        {/* Meta strip — matches iOS metaStrip */}
        <Card>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-md">
            {totalTime > 0 && (
              <div className="text-center">
                <Clock size={18} className="text-primary mx-auto mb-xs" />
                <p className="text-title-3 font-bold text-text-primary">{totalTime}</p>
                <p className="text-caption text-text-secondary">Total (min)</p>
              </div>
            )}
            {(recipe.prepTimeMinutes || recipe.prepTime) > 0 && (
              <div className="text-center">
                <UtensilsCrossed size={18} className="text-secondary mx-auto mb-xs" />
                <p className="text-title-3 font-bold text-text-primary">{recipe.prepTimeMinutes || recipe.prepTime}</p>
                <p className="text-caption text-text-secondary">Prep (min)</p>
              </div>
            )}
            {(recipe.cookTimeMinutes || recipe.cookTime) > 0 && (
              <div className="text-center">
                <Flame size={18} className="text-accent mx-auto mb-xs" />
                <p className="text-title-3 font-bold text-text-primary">{recipe.cookTimeMinutes || recipe.cookTime}</p>
                <p className="text-caption text-text-secondary">Cook (min)</p>
              </div>
            )}
            {recipe.servings > 0 && (
              <div className="text-center">
                <Users size={18} className="text-secondary mx-auto mb-xs" />
                <p className="text-title-3 font-bold text-text-primary">{recipe.servings}</p>
                <p className="text-caption text-text-secondary">Servings</p>
              </div>
            )}
            {recipe.difficulty && (
              <div className="text-center">
                <ChefHat size={18} className="text-primary mx-auto mb-xs" />
                <p className="text-title-3 font-bold text-text-primary capitalize">{recipe.difficulty}</p>
                <p className="text-caption text-text-secondary">Difficulty</p>
              </div>
            )}
          </div>
          {recipe.rating > 0 && (
            <div className="flex items-center gap-xs mt-md pt-md border-t border-text-tertiary/10">
              {[1, 2, 3, 4, 5].map(star => (
                <Star key={star} size={16} className={star <= Math.round(recipe.rating) ? 'text-accent fill-accent' : 'text-text-tertiary'} />
              ))}
              <span className="text-callout font-semibold text-text-primary ml-xs">{Number(recipe.rating).toFixed(1)}</span>
              {recipe.reviewCount > 0 && (
                <button onClick={() => navigateTo('reviews')} className="text-caption text-primary hover:underline">
                  ({recipe.reviewCount} reviews)
                </button>
              )}
            </div>
          )}
        </Card>

        {/* Description — infoCard style */}
        {recipe.description && (
          <InfoCard title="About this recipe" icon={Info}>
            <p className="text-body text-text-secondary leading-relaxed">{recipe.description}</p>
          </InfoCard>
        )}

        {/* Dietary tags — infoCard style */}
        {recipe.dietaryTags?.length > 0 && (
          <InfoCard title="Dietary Info" icon={Leaf}>
            <div className="flex flex-wrap gap-sm">
              {recipe.dietaryTags.map(tag => {
                const key   = tag.toLowerCase().replace(/[_\s-]/g, '');
                const color = DIETARY_TAG_COLORS[key] || '#6b7280';
                return (
                  <span
                    key={tag}
                    className="px-md py-xs rounded-full text-caption font-semibold"
                    style={{ color, background: `${color}18` }}
                  >
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </span>
                );
              })}
            </div>
          </InfoCard>
        )}

        {/* Recipe tags — infoCard style */}
        {recipe.recipeTags?.length > 0 && (
          <InfoCard title="Tags" icon={Tag}>
            <div className="flex flex-wrap gap-sm">
              {recipe.recipeTags.map(tag => (
                <span
                  key={tag}
                  className="px-md py-xs rounded-full text-caption font-semibold text-primary"
                  style={{ background: 'rgba(var(--color-primary-rgb),0.08)' }}
                >
                  {tag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              ))}
            </div>
          </InfoCard>
        )}
      </div>

      {/* ── INGREDIENTS ── */}
      <div ref={ingredientsRef} id="rdv-ingredients" className="mb-xl scroll-mt-16">

        {/* Section header with serving stepper */}
        <div className="flex items-center justify-between mb-lg">
          <h2 className="text-title-2 font-bold text-text-primary">Ingredients</h2>
          {recipe.servings > 0 && (
            <div
              className="flex items-center gap-md rounded-large px-md py-sm bg-surface"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.15)' }}
            >
              <span className="text-caption text-text-secondary">Servings</span>
              <button
                onClick={() => setCurrentServings(s => Math.max(1, s - 1))}
                className={`w-7 h-7 rounded-full flex items-center justify-center font-bold transition-all ${
                  currentServings > 1
                    ? 'bg-primary/10 text-primary hover:bg-primary/20'
                    : 'bg-text-tertiary/10 text-text-tertiary cursor-not-allowed'
                }`}
                disabled={currentServings <= 1}
              >
                –
              </button>
              <span className="text-callout font-bold text-primary w-7 text-center">{currentServings}</span>
              <button
                onClick={() => setCurrentServings(s => s + 1)}
                className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold hover:bg-primary/20 transition-all"
              >
                +
              </button>
            </div>
          )}
        </div>

        {ingredients.length > 0 ? (
          <Card>
            {/* Add to list row */}
            <div className="flex items-center justify-between mb-lg pb-lg border-b border-text-tertiary/10">
              <div>
                <p className="text-callout font-semibold text-text-primary">
                  {ingredients.length} ingredient{ingredients.length !== 1 ? 's' : ''}
                </p>
                {ingredients.some(i => i.linkedProductId) && (
                  <p className="text-caption text-primary mt-xs flex items-center gap-xs">
                    <Link size={11} />
                    {ingredients.filter(i => i.linkedProductId).length} linked to products
                  </p>
                )}
              </div>
              <div className="flex items-center gap-sm flex-wrap justify-end">
                <Button variant="outline" size="small" onClick={() => setShowAddToList(true)}>
                  <ShoppingCart size={15} className="mr-xs" />
                  Add to List
                </Button>
                {steps.length > 0 && !cookingActive && (
                  <StartRecipeButton onClick={startCooking} variant="inline" />
                )}
                {cookingActive && (
                  <button
                    onClick={() => cookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="flex items-center gap-xs px-md py-sm rounded-large text-caption font-semibold bg-success/10 text-success border border-success/30 hover:bg-success/15 transition-all"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                    Step {currentStepIndex + 1} of {steps.length}
                  </button>
                )}
              </div>
            </div>

            {/* Ingredient rows — tappable checkboxes with strikethrough, matches iOS */}
            <ul className="space-y-xs">
              {ingredients.map((ingredient, index) => {
                const isStr      = typeof ingredient === 'string';
                const id         = ingredient.id ?? index;
                const name       = isStr ? ingredient : ingredient.name;
                const qty        = isStr ? null : (ingredient.quantity || ingredient.amount);
                const unit       = isStr ? null : ingredient.unit;
                const notes      = isStr ? null : ingredient.notes;
                const isOptional = isStr ? false : ingredient.isOptional;
                const isLinked   = !isStr && !!ingredient.linkedProductId;
                const isChecked  = checkedIngredients.has(id);

                return (
                  <li key={id}>
                    <button
                      onClick={() => toggleIngredient(id)}
                      className={`w-full flex items-start gap-md px-md py-sm rounded-large text-left transition-all ${
                        isChecked
                          ? 'bg-primary/4 border border-primary/15'
                          : 'bg-transparent border border-primary/7 hover:border-primary/15'
                      }`}
                      style={{ borderWidth: '1px' }}
                    >
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mt-xs">
                        {isChecked ? (
                          <CheckCircle2 size={20} className="text-primary" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-text-tertiary/30" />
                        )}
                      </div>

                      {/* Product image if linked */}
                      {isLinked && ingredient.linkedProductImageURL && (
                        <div className="w-8 h-8 rounded-medium bg-surface border border-text-tertiary/10 flex items-center justify-center flex-shrink-0 overflow-hidden mt-xs">
                          <img src={ingredient.linkedProductImageURL} alt={name} className="w-full h-full object-contain p-xs" />
                        </div>
                      )}

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <span className={`text-body text-text-primary ${isChecked ? 'line-through text-text-tertiary' : ''}`}>
                          {qty ? `${scaleQty(qty, currentServings, baseServings)} ` : ''}
                          {unit ? `${unit} ` : ''}
                          <span className="font-medium">{name}</span>
                        </span>
                        {notes && <span className="text-caption text-text-secondary ml-xs italic">{notes}</span>}
                        {isOptional && (
                          <span className="ml-xs px-xs py-xs bg-text-tertiary/10 text-text-tertiary text-caption rounded-small">optional</span>
                        )}
                        {isLinked && (
                          <p className="text-caption text-primary mt-xs flex items-center gap-xs">
                            <Link size={10} />{ingredient.linkedProductName}
                          </p>
                        )}
                      </div>

                      {/* Quick add button */}
                      <button
                        onClick={e => { e.stopPropagation(); setShowAddToList(true); }}
                        className="w-7 h-7 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center flex-shrink-0 transition-all mt-xs"
                        title="Add to shopping list"
                      >
                        <Plus size={14} className="text-primary" />
                      </button>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Allergen warning — moved into ingredients section, matches iOS */}
            {recipe.allergens?.length > 0 && (
              <div
                className="mt-lg flex items-start gap-sm p-md rounded-large"
                style={{ background: 'rgba(var(--color-warning),0.07)', border: '1px solid rgba(var(--color-warning),0.2)' }}
              >
                <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-xs" />
                <div>
                  <p className="text-callout font-semibold text-warning mb-xs">Contains allergens</p>
                  <p className="text-body text-text-secondary">{recipe.allergens.join(', ')}</p>
                </div>
              </div>
            )}
          </Card>
        ) : (
          <p className="text-body text-text-secondary">No ingredients listed.</p>
        )}
      </div>

      {/* ── INSTRUCTIONS ── */}
      <div ref={instructionsRef} id="rdv-instructions" className="mb-xl scroll-mt-16">
        <div className="flex items-center justify-between mb-lg">
          <h2 className="text-title-2 font-bold text-text-primary">Instructions</h2>
          {stepsTotal > 0 && (
            <div className="flex items-center gap-sm">
              <span className="text-caption text-text-secondary">{stepsComplete}/{stepsTotal} complete</span>
              <div className="w-24 h-1.5 bg-text-tertiary/15 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${stepsTotal > 0 ? (stepsComplete / stepsTotal) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {steps.length > 0 ? (
          <div className="space-y-md">
            {steps.map((step, index) => {
              const s      = getStepData(step, index);
              const isDone = checkedSteps.has(s.id);
              return (
                <div
                  key={s.id}
                  className={`rounded-large overflow-hidden transition-opacity ${isDone ? 'opacity-60' : ''}`}
                  style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                >
                  {s.imageURL && (
                    <div className="w-full aspect-video overflow-hidden bg-surface">
                      <img src={s.imageURL} alt={`Step ${s.order}`} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-lg">
                    <div className="flex gap-md">
                      <button onClick={() => toggleStep(s.id)} className="flex-shrink-0 mt-xs transition-all">
                        {isDone ? (
                          <CheckCircle2 size={28} className="text-primary fill-primary/10" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                            <span className="text-caption font-bold text-white">{s.order}</span>
                          </div>
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-body text-text-primary leading-relaxed ${isDone ? 'line-through text-text-tertiary' : ''}`}>
                          {s.instruction}
                        </p>
                        {s.timerMins && (
                          <div className="flex items-center gap-xs mt-md px-md py-xs bg-primary/5 rounded-small w-fit">
                            <Timer size={14} className="text-primary" />
                            <span className="text-caption font-semibold text-primary">{s.timerMins} min</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {allStepsDone && (
              <div
                className="p-lg rounded-large flex items-center gap-md"
                style={{ background: 'rgba(var(--color-success),0.08)', border: '2px solid rgba(var(--color-success),0.3)' }}
              >
                <CheckCircle2 size={28} className="text-success flex-shrink-0" />
                <div>
                  <p className="text-callout font-bold text-success">All steps complete!</p>
                  <p className="text-caption text-text-secondary mt-xs">Time to enjoy your meal.</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-body text-text-secondary">No instructions listed.</p>
        )}
      </div>

      {/* ── NUTRITION ── */}
      {hasNutrition && (
        <div ref={nutritionRef} id="rdv-nutrition" className="mb-xl scroll-mt-16">
          <h2 className="text-title-2 font-bold text-text-primary mb-lg">Nutrition</h2>
          <InfoCard title="Per serving" icon={BarChart2}>
            {/* 2-column tile grid — matches iOS nutritionStat grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-md">
              {recipe.nutrition.calories      != null && <NutritionTile label="Calories"      value={recipe.nutrition.calories}                        unit="kcal" />}
              {recipe.nutrition.protein       != null && <NutritionTile label="Protein"        value={Number(recipe.nutrition.protein).toFixed(1)}       unit="g"    />}
              {recipe.nutrition.carbohydrates != null && <NutritionTile label="Carbs"          value={Number(recipe.nutrition.carbohydrates).toFixed(1)} unit="g"    />}
              {recipe.nutrition.fat           != null && <NutritionTile label="Fat"            value={Number(recipe.nutrition.fat).toFixed(1)}           unit="g"    />}
              {recipe.nutrition.fiber         != null && <NutritionTile label="Fiber"          value={Number(recipe.nutrition.fiber).toFixed(1)}         unit="g"    />}
              {recipe.nutrition.sugar         != null && <NutritionTile label="Sugar"          value={Number(recipe.nutrition.sugar).toFixed(1)}         unit="g"    />}
              {recipe.nutrition.sodium        != null && <NutritionTile label="Sodium"         value={Math.round(recipe.nutrition.sodium)}               unit="mg"   />}
            </div>
          </InfoCard>
        </div>
      )}

      {/* ── REVIEWS ── */}
      <div ref={reviewsRef} id="rdv-reviews" className="mb-xl scroll-mt-16">
        <h2 className="text-title-2 font-bold text-text-primary mb-lg">Reviews</h2>

        {reviews.length > 0 && (
          <Card className="mb-lg">
            <div className="flex items-start gap-xl">
              <div className="text-center flex-shrink-0">
                <p className="text-display font-bold text-primary">{Number(recipe.rating || 0).toFixed(1)}</p>
                <div className="flex items-center gap-xs justify-center my-xs">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} size={14} className={s <= Math.round(recipe.rating) ? 'text-accent fill-accent' : 'text-text-tertiary'} />
                  ))}
                </div>
                <p className="text-caption text-text-tertiary">{reviews.length} reviews</p>
              </div>
              <div className="flex-1 space-y-xs">
                {[5, 4, 3, 2, 1].map(star => {
                  const { count, pct } = ratingBreakdown(star);
                  return (
                    <div key={star} className="flex items-center gap-sm">
                      <span className="text-caption text-text-tertiary w-3">{star}</span>
                      <Star size={11} className="text-accent fill-accent flex-shrink-0" />
                      <div className="flex-1 h-1.5 bg-text-tertiary/15 rounded-full overflow-hidden">
                        <div className="h-full bg-accent rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-caption text-text-tertiary w-4 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {isAuthenticated && !hasUserReviewed && (
          <button
            onClick={() => setShowAddReview(true)}
            className="w-full p-lg rounded-large bg-surface flex items-center gap-md hover:bg-primary/5 transition-all mb-lg"
            style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
          >
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <MessageSquare size={16} className="text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-callout font-semibold text-text-primary">Write a Review</p>
              <p className="text-caption text-text-secondary">Share your experience with this recipe</p>
            </div>
            <Star size={16} className="text-text-tertiary flex-shrink-0" />
          </button>
        )}

        {isAuthenticated && hasUserReviewed && (
          <div
            className="p-lg rounded-large flex items-center gap-md mb-lg"
            style={{ background: 'rgba(var(--color-success),0.05)', border: '1px solid rgba(var(--color-success),0.2)' }}
          >
            <Check size={18} className="text-success flex-shrink-0" />
            <p className="text-callout font-semibold text-success">You have reviewed this recipe</p>
          </div>
        )}

        {reviewsLoading ? (
          <div className="flex justify-center py-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-xl text-center rounded-large bg-surface" style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}>
            <Star size={32} className="text-text-tertiary/30 mx-auto mb-md" />
            <p className="text-callout font-semibold text-text-primary mb-xs">No reviews yet</p>
            <p className="text-body text-text-secondary">Be the first to review this recipe.</p>
          </div>
        ) : (
          <div className="space-y-md">
            {reviews.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                hasVoted={userVotedReviews.has(review.id)}
                onVote={() => handleVote(review.id)}
                isAuthenticated={isAuthenticated}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── MODALS ── */}
      {showShare && (
        <RecipeShareModal recipe={recipe} onClose={() => setShowShare(false)} />
      )}

      {showDeleteConfirm && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-lg">
          <Card className="max-w-sm w-full">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-md">
                <Trash2 size={24} className="text-error" />
              </div>
              <h3 className="text-title-3 font-bold text-text-primary mb-sm">Delete Recipe?</h3>
              <p className="text-body text-text-secondary mb-lg">This will remove your recipe. This action cannot be undone.</p>
              <div className="flex gap-sm">
                <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</Button>
                <Button variant="primary" className="flex-1 bg-error border-error hover:bg-error/90" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </Card>
        </div>,
        document.body
      )}

      {showAddToList && (
        <AddToListModal recipe={{ ...recipe, ingredients }} onClose={() => setShowAddToList(false)} />
      )}
      {showAddReview && (
        <AddReviewModal
          recipeId={recipe.firestoreId || recipe.id}
          onClose={() => setShowAddReview(false)}
          onSubmitted={() => { setShowAddReview(false); loadReviews(); }}
        />
      )}
    </div>
  );
};

export default RecipeDetailView;